import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * How tall a form control is. `dense` (the default): 44px (--size-touch-min) with 16px text on phones (no iOS zoom), 36px with 13px text
 * from `md` up, inside the desktop app shell, where every mockup puts its fields in cards, sheets and
 * filter rows (UX audit polish). `touch`: 44px at every width, for the signed-out screens (sign in, sign up,
 * invite) where the field is the whole page.
 *
 * The height is a custom property (`--control-h`) on purpose: a caller's `h-8` (the compact search box)
 * merges against `h-(--control-h)` and wins at every width, which a `md:h-9` class would not let it do.
 */
export type ControlDensity = 'dense' | 'touch';

export function controlHeight(density: ControlDensity = 'dense'): string {
  return cn(
    'h-(--control-h) text-(length:--text-md) [--control-h:var(--spacing-touch)]',
    density === 'dense' && 'md:text-sm md:[--control-h:calc(var(--spacing)*9)]',
  );
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  density?: ControlDensity;
}

/* shadcn/ui Input. The border uses --input, which shadcn.css points at surface.border-strong: that
 * token exists at 3:1 precisely because a form control border is WCAG 1.4.11. */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type = 'text', density = 'dense', ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type={type}
      className={cn(
        'w-full rounded-md border border-input bg-card px-3 text-foreground',
        controlHeight(density),
        'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
});
